import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvironmentService } from '../environment/environment.service';

interface IdTokenDecoded {
  at_hash: string
  sub: string
  "cognito:groups": string[]
  email_verified: boolean
  iss: string
  "cognito:username": string
  given_name: string
  middle_name: string
  origin_jti: string
  aud: string
  identities: {
    dateCreated: string
    userId: string
    providerName: string
    providerType: string
    issuer: string
    primary: string
  }[]
  token_use: string
  auth_time: number
  exp: number
  iat: number
  jti: string
  email: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authStatus = new BehaviorSubject<boolean>(false);
  public readonly isAuthenticated$: Observable<boolean> = this.authStatus.asObservable();
  private readonly clientId: string;
  private readonly clientSecret: string;
  private readonly redirectUri: string;
  private readonly cognitoDomain: string;
  private tokenEndpoint: string;
  private readonly scope: string;

  private currentUserSubject = new BehaviorSubject<IdTokenDecoded | null>(null);
  public readonly currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private envService: EnvironmentService
  ) {
    this.clientId = this.envService.get('COGNITO_CLIENT_ID');
    this.clientSecret = this.envService.get('COGNITO_CLIENT_SECRET');
    this.redirectUri = this.envService.get('COGNITO_REDIRECT_URI');
    this.cognitoDomain = this.envService.get('COGNITO_DOMAIN');
    this.tokenEndpoint = `${this.cognitoDomain}/oauth2/token`;
    this.scope = this.envService.get('COGNITO_SCOPE');
  }

  async checkAuth(): Promise<boolean> {
    const token = await this.getAccessToken();

    if (token) {
      const decodedToken = await this.decodeToken();
      this.currentUserSubject.next(decodedToken);
      this.authStatus.next(true);
      return true;
    }

    const code = this.route.snapshot.queryParamMap.get('code');

    if (code) {
      await this.exchangeCodeForToken(code);
      return true;
    }

    this.login();
    return false;
  }

  login(): void {
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'code',
      redirect_uri: this.redirectUri,
      scope: this.scope,
    });

    window.location.href = `${this.cognitoDomain}/login?${params}`;
  }

  async exchangeCodeForToken(code: string): Promise<void> {
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: this.redirectUri,
      client_id: this.clientId,
    });

    const credentials = btoa(`${this.clientId}:${this.clientSecret}`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Basic ${credentials}`,
    });

    try {
      const res: any = await this.http.post(this.tokenEndpoint, body.toString(), { headers }).toPromise();

      if (!res.access_token) {
        return;
      }

      this.storeTokens(res);
      this.authStatus.next(true);
    } catch (error) {
      console.error('Erro ao trocar código pelo token:', error);
    }
  }

  async getAccessToken(): Promise<string | null> {
    const tokens = this.getStoredTokens();

    if (!tokens) {
      return null;
    }

    if (this.isTokenExpired(tokens.expires_at)) {
      return await this.refreshToken();
    }

    return tokens.id_token;
  }

  async refreshToken(): Promise<string | null> {
    const tokens = this.getStoredTokens();

    if (!tokens?.refresh_token) {
      this.logout();
      return null;
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: tokens.refresh_token,
      client_id: this.clientId,
    });

    const credentials = btoa(`${this.clientId}:${this.clientSecret}`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Basic ${credentials}`,
    });

    try {
      const res: any = await this.http.post(this.tokenEndpoint, body.toString(), { headers }).toPromise();

      if (!res.access_token) {
        this.logout();
        return null;
      }

      this.storeTokens(res);
      return res.access_token;
    } catch (error) {
      this.logout();
      return null;
    }
  }

  private storeTokens(res: any): void {
    const expires_at = Date.now() + res.expires_in * 1000;
    const tokens = JSON.stringify({
      access_token: res.access_token,
      id_token: res.id_token,
      refresh_token: res.refresh_token,
      expires_at,
      token_type: res.token_type,
    });

    localStorage.setItem('user_logged', tokens);
    this.decodeToken()
  }

  private getStoredTokens(): any | null {
    const tokens = localStorage.getItem('user_logged');
    return tokens ? JSON.parse(tokens) : null;
  }

  private isTokenExpired(expires_at: number): boolean {
    return Date.now() >= expires_at;
  }

  public async decodeToken(): Promise<IdTokenDecoded | null> {
    const token = await this.getAccessToken();

    if (!token) {
      return null;
    }
    
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Token inválido, formato JWT esperado');
        return null;
      }
      
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const decodedToken = JSON.parse(jsonPayload) as IdTokenDecoded;
      this.currentUserSubject.next(decodedToken);
      return decodedToken;
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  public getCurrentUser(): IdTokenDecoded | null {
    return this.currentUserSubject.getValue();
  }

  logout(): void {
    localStorage.removeItem('user_logged');
    this.authStatus.next(false);
    this.router.navigate(['/']);
  }
}
